import type { RouteRecordRaw, RouterScrollBehavior } from "vue-router";
import {
  BLOG_LOCALE_PREFIXES,
  LOCALES,
  ROUTE_MANIFEST,
  type Locale,
  type RouteName,
} from "@/seo/routes-manifest";

const pageLoaders: Record<RouteName, () => Promise<unknown>> = {
  home: () => import("@/pages/home-page.vue"),
  philosophy: () => import("@/pages/philosophy.vue"),
  features: () => import("@/pages/features.vue"),
};

const manifestRoutes: RouteRecordRaw[] = (
  Object.entries(ROUTE_MANIFEST) as [RouteName, Record<Locale, string>][]
).flatMap(([name, pathsByLocale]) =>
  LOCALES.map((locale) => ({
    path: pathsByLocale[locale],
    name: `${name}.${locale}`,
    component: pageLoaders[name],
    meta: { locale, routeName: name },
  })),
);

const legalRoutes: RouteRecordRaw[] = [
  {
    path: "/terms-of-sale",
    name: "terms-of-sale",
    component: () => import("@/pages/terms-of-sale.vue"),
    meta: { locale: "en", routeName: "terms-of-sale" },
  },
  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: () => import("@/pages/privacy-policy.vue"),
    meta: { locale: "en", routeName: "privacy-policy" },
  },
];

const standaloneRoutes: RouteRecordRaw[] = [
  {
    path: "/early-access",
    name: "early-access",
    component: () => import("@/pages/early-access.vue"),
    meta: { locale: "en", routeName: "early-access" },
  },
];

const blogRoutes: RouteRecordRaw[] = [
  {
    path: "/blog",
    name: "blog-index.en",
    component: () => import("@/pages/blog-index.vue"),
    meta: { locale: "en", routeName: "blog-index" },
  },
  {
    path: "/blog/:slug",
    name: "blog-post.en",
    component: () => import("@/pages/blog-post.vue"),
    meta: { locale: "en", routeName: "blog-post" },
  },
  ...BLOG_LOCALE_PREFIXES.flatMap((prefix): RouteRecordRaw[] => {
    const locale = prefix.slice(1);
    return [
      {
        path: `${prefix}/blog`,
        name: `blog-index.${locale}`,
        component: () => import("@/pages/blog-index.vue"),
        meta: { locale, routeName: "blog-index" },
      },
      {
        path: `${prefix}/blog/:slug`,
        name: `blog-post.${locale}`,
        component: () => import("@/pages/blog-post.vue"),
        meta: { locale, routeName: "blog-post" },
      },
    ];
  }),
];

export const routes: RouteRecordRaw[] = [
  ...manifestRoutes,
  ...legalRoutes,
  ...standaloneRoutes,
  ...blogRoutes,
];

export const scrollBehavior: RouterScrollBehavior = (to, _from, savedPosition) => {
  if (savedPosition) return savedPosition;
  if (to.hash) return { el: to.hash, top: 80 };
  return { top: 0 };
};
